/**
 * PKR money handling.
 *
 * STORAGE RULE: every amount in the database is an INTEGER number of PAISA.
 * Never a float. Floats silently lose rupees on a 30-way expense split, and
 * this is a financial ledger. Convert at the display edge only.
 *
 * FORMATTING RULE: Pakistani traders read and speak in lakh and crore
 * (1,23,45,678) but ICU's `en-PK` locale produces Western grouping
 * (12,345,678). Verified on this machine:
 *
 *   en-PK  -> "Rs 12,345,678"
 *   en-IN  -> "PKR 1,23,45,678"
 *
 * So we borrow `en-IN` purely for its digit grouping and attach the "Rs"
 * prefix ourselves. Western grouping stays available as a user setting for
 * anyone who prefers it.
 */

export type Grouping = 'indian' | 'western'

export const PAISA_PER_RUPEE = 100
/** Exported only because `parse.ts` reads the same shorthand back. */
export const LAKH = 100_000
export const CRORE = 10_000_000

const GROUPERS: Record<Grouping, Intl.NumberFormat> = {
  indian: new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }),
  western: new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }),
}

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

export function rupeesToPaisa(rupees: number): number {
  return Math.round(rupees * PAISA_PER_RUPEE)
}

export function paisaToRupees(paisa: number): number {
  return paisa / PAISA_PER_RUPEE
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export interface FormatOptions {
  grouping?: Grouping
  /** Include the "Rs" prefix. Default true. */
  symbol?: boolean
  /** Render a leading "+" for positive amounts. Useful for profit figures. */
  signed?: boolean
}

/**
 * Full-precision display: "Rs 1,23,45,678".
 * Rounds to whole rupees — paisa are not used in cattle trading and showing
 * them makes every screen noisier for no benefit.
 */
export function formatPKR(paisa: number, opts: FormatOptions = {}): string {
  const { grouping = 'indian', symbol = true, signed = false } = opts
  const rupees = Math.round(paisaToRupees(paisa))
  const negative = rupees < 0
  const digits = GROUPERS[grouping].format(Math.abs(rupees))

  const sign = negative ? '-' : signed ? '+' : ''
  return symbol ? `${sign}Rs ${digits}` : `${sign}${digits}`
}

/**
 * Compact display for dashboard cards and chart axes: "Rs 1.23 crore".
 * This is how the trader actually thinks about inventory value — a card
 * reading "Rs 2,34,56,000" takes real effort to read at a glance.
 */
export function formatPKRShort(paisa: number, opts: FormatOptions = {}): string {
  const { symbol = true, signed = false } = opts
  const rupees = paisaToRupees(paisa)
  const abs = Math.abs(rupees)

  if (abs < LAKH) return formatPKR(paisa, opts)

  const [value, unit] = abs >= CRORE ? [rupees / CRORE, 'crore'] : [rupees / LAKH, 'lakh']

  // "Rs 1 crore" reads better than "Rs 1.00 crore".
  const text = trimZeros(Math.abs(value).toFixed(2))
  const sign = rupees < 0 ? '-' : signed ? '+' : ''

  return `${sign}${symbol ? 'Rs ' : ''}${text} ${unit}`
}

function trimZeros(s: string): string {
  return s.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1')
}

/** "45 days", "1 day" — used everywhere alongside money. */
export function formatDays(days: number): string {
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

/** Rate display: "Rs 400/day". */
export function formatRatePerDay(paisaPerDay: number, opts: FormatOptions = {}): string {
  return `${formatPKR(paisaPerDay, opts)}/day`
}
